import React, { useEffect } from "react";
import _ from "lodash";
import { 
  Space, 
  Card, 
  Carousel, 
  Tag, 
  Tabs, 
  Spin, 
  Result, 
  Row, 
  Col,
  Typography,
  Badge,
  Breadcrumb,
  Divider,
  Modal,
  Dropdown,
  Menu,
  Button,
  message
} from "antd";
import { 
  MoreOutlined, 
  LinkOutlined, 
  DatabaseOutlined, 
  CopyOutlined,
  DownloadOutlined,
  FolderOpenOutlined
} from "@ant-design/icons";
import { LazyLoadImage } from "react-lazy-load-image-component";
import InfiniteScroll from "react-infinite-scroll-component";
import apiConfig from "../../api_config.json";
import { useAppContext } from "../../context";
import { useGiottoData } from "../../hooks/useGiottoData";
import formatDistance from "date-fns/formatDistance";
import { it } from "date-fns/locale";
import EdgeResizer from "../common/utils/EdgeResizer";

const { TabPane } = Tabs;
const { Title } = Typography;

const AssetsPreview = ({ imageUrl, isProd }) => {
  const [previewVisible, setPreviewVisible] = React.useState(false);
  
  if (!imageUrl) return null;
  
  // Get optimized image URL through EdgeResizer to scale width to 300px
  const getOptimizedImageUrl = (url) => {
    try {
      const env = isProd ? "prod" : "dev";
      const edgeResizer = new EdgeResizer(apiConfig.files[env], url);
      return edgeResizer.scaleToWidth(300).toString();
    } catch (error) {
      console.error('Error processing image URL:', error);
      return url; // Fallback to original URL
    }
  };
  
  return (
    <>
      <Col xs={24} sm={12} md={8} lg={6} xl={4} style={{ marginBottom: 16 }}>
        <Card 
          hoverable
          cover={
            <div 
              onClick={() => setPreviewVisible(true)}
              style={{ cursor: 'pointer' }}
            >
              <LazyLoadImage 
                width="100%" 
                height={250}
                style={{ objectFit: 'contain', backgroundColor: '#f5f5f5' }}
                src={getOptimizedImageUrl(imageUrl)} 
                alt="Giotto asset" 
              />
            </div>
          }
        />
      </Col>
      
      <Modal
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width="90vw"
        style={{ top: 20 }}
        bodyStyle={{ padding: 20, textAlign: 'center' }}
      >
        <img 
          src={imageUrl} 
          alt="Giotto asset preview" 
          style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
        />
      </Modal>
    </>
  );
};

const GiottoPreview = ({
  title,
  url,
  pages = [],
  createdAt,
  updatedAt,
  status,
  orgName,
  orgUrl,
  showOrg = false,
  isProd,
  objectId
}) => {
  const [previewVisible, setPreviewVisible] = React.useState(false);
  const [currentImageUrl, setCurrentImageUrl] = React.useState(null);
  const validPages = pages.filter(page => page?.preview?.url);
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(url).then(() => {
      message.success('Link progetto copiato negli appunti');
    }).catch(() => {
      message.error('Errore durante la copia del link');
    });
  };
  
  const handleDownloadImage = (imageUrl, filename) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = filename || 'giotto-image.png';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <>
      <Col xs={24} sm={12} md={8} lg={6} style={{ marginBottom: 16 }}>
        <Card
          title={title || "Progetto"}
          extra={
            <Space>
              <Dropdown 
                overlay={
                  <Menu>
                    <Menu.Item 
                      key="open-project" 
                      icon={<FolderOpenOutlined />}
                      onClick={() => window.open(url, '_blank')}
                    >
                      Apri progetto in Giotto
                    </Menu.Item>
                    <Menu.Item 
                      key="copy-link" 
                      icon={<CopyOutlined />}
                      onClick={handleCopyLink}
                    >
                      Copia link progetto
                    </Menu.Item>
                    {validPages.length > 0 && validPages[0]?.preview?.url && (
                      <Menu.Item 
                        key="download-preview" 
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownloadImage(validPages[0].preview.url, `preview_${objectId}.png`)}
                      >
                        Scarica anteprima
                      </Menu.Item>
                    )}
                    {showOrg && orgUrl && (
                      <>
                        <Menu.Divider />
                        <Menu.Item 
                          key="org-details" 
                          icon={<LinkOutlined />}
                          onClick={() => window.open(orgUrl, '_blank')}
                        >
                          Dettagli organizzazione
                        </Menu.Item>
                      </>
                    )}
                    {objectId && (
                      <>
                        <Menu.Divider />
                        <Menu.Item 
                          key="parse-dashboard" 
                          icon={<DatabaseOutlined />}
                          onClick={() => window.open(`${apiConfig.parseDashboard[isProd ? "prod" : "dev"]}/browser/editor_canvases?filters=[{"field":"objectId","constraint":"eq","compareTo":"${objectId}"}]`, '_blank')}
                        >
                          Apri in Parse Dashboard
                        </Menu.Item>
                      </>
                    )}
                  </Menu>
                } 
                trigger={['click']}
              >
                <Button icon={<MoreOutlined />} />
              </Dropdown>
            </Space>
          }
          hoverable
          style={{ height: '100%' }}
          bodyStyle={{ padding: 12 }}
        >
          {validPages.length > 0 ? (
            <Carousel autoplay style={{ marginBottom: 12 }}>
              {validPages.map((page, index) => {
                // Get optimized image URL through EdgeResizer to scale width to 300px
                const getOptimizedImageUrl = (url) => {
                  try {
                    const env = isProd ? "prod" : "dev";
                    const edgeResizer = new EdgeResizer(apiConfig.files[env], url);
                    return edgeResizer.scaleToWidth(300).toString();
                  } catch (error) {
                    console.error('Error processing image URL:', error);
                    return url; // Fallback to original URL
                  }
                };
                
                return (
                  <div 
                    key={index}
                    onClick={() => {
                      setCurrentImageUrl(page.preview.url);
                      setPreviewVisible(true);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <LazyLoadImage
                      width="100%"
                      height={200}
                      style={{ objectFit: 'contain', backgroundColor: '#f5f5f5' }}
                      src={getOptimizedImageUrl(page.preview.url)}
                      alt={page.objectId || `Page ${index}`}
                    />
                  </div>
                );
              })}
            </Carousel>
          ) : (
            <div 
              style={{ 
                height: 200, 
                backgroundColor: '#f5f5f5', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                marginBottom: 12
              }}
            >
              Nessuna anteprima disponibile
            </div>
          )}
          
          <div style={{ 
            marginTop: "10px",
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              <Tag style={{ width: 'fit-content' }}>
                aggiornato {formatDistance(new Date(updatedAt), new Date(), { locale: it })}
              </Tag>
              <Tag style={{ width: 'fit-content' }}>
                creato {formatDistance(new Date(createdAt), new Date(), { locale: it })}
              </Tag>
              <Tag
                color={status === "hidden" ? "volcano" : "green"}
                style={{ width: 'fit-content' }}
              >
                {status === "hidden" ? "ELIMINATO" : "ATTIVO"}
              </Tag>
            </div>
            {showOrg && orgName && (
              <Tag 
                color="blue" 
                style={{ 
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={orgName}
              >
                {orgName}
              </Tag>
            )}
          </div>
        </Card>
      </Col>
      
      <Modal
        open={previewVisible}
        onCancel={() => {
          setPreviewVisible(false);
          setCurrentImageUrl(null);
        }}
        footer={null}
        width="90vw"
        style={{ top: 20 }}
        bodyStyle={{ padding: 20, textAlign: 'center' }}
      >
        {currentImageUrl && (
          <img 
            src={currentImageUrl} 
            alt="Giotto preview" 
            style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
          />
        )}
      </Modal>
    </>
  );
};

/**
 * Consolidated Giotto Projects component
 * @param {string} orgId - Optional organization ID. If not provided, shows all organizations
 * @param {boolean} showBreadcrumb - Whether to show breadcrumb navigation
 * @param {boolean} showTitle - Whether to show the main title
 * @param {boolean} showTabs - Whether to show tabs (templates, images, backgrounds)
 */
const GiottoProjects = ({ 
  orgId = null, 
  showBreadcrumb = true,
  showTitle = true,
  showTabs = true
}) => {
  const { isProd } = useAppContext();
  const isGlobalView = !orgId;
  
  // For global view, we need to modify the hook or create a new one
  const { loading, initialFetchDone, data, counts, hasMore, fetchAllInitialData, loadMore } = useGiottoData(orgId);

  useEffect(() => {
    fetchAllInitialData();
  }, [fetchAllInitialData]);

  // Show loading spinner only on initial load
  if (!initialFetchDone && loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const {
    editorCanvases,
    editorTemplates,
    giottoImages,
    giottoBackgrounds,
  } = data;

  const hasNoData = 
    counts.editorCanvases === 0 && 
    counts.editorTemplates === 0 && 
    counts.giottoImages === 0 && 
    counts.giottoBackgrounds === 0;

  // Only show "no data" message after initial fetch is complete
  if (hasNoData && initialFetchDone && !loading) {
    return (
      <Result
        status="info"
        title="Nessun contenuto Giotto trovato"
        subTitle={isGlobalView 
          ? "Non ci sono progetti Giotto nel sistema."
          : "Non ci sono progetti, template, immagini o sfondi per questa organizzazione."
        }
      />
    );
  }

  const renderInfiniteScrollContent = (items, dataType, renderItem) => (
    <InfiniteScroll
      dataLength={items.length}
      next={() => loadMore(dataType)}
      hasMore={hasMore[dataType]}
      loader={<Spin size="large" style={{ display: 'block', textAlign: 'center', margin: '20px 0' }} />}
      endMessage={
        items.length > 0 && (
          <div style={{ textAlign: 'center', margin: '20px 0', color: '#999' }}>
            Non ci sono altri elementi da caricare
          </div>
        )
      }
    >
      <Row gutter={[16, 16]}>
        {items.map(renderItem)}
      </Row>
    </InfiniteScroll>
  );

  const renderProjectsContent = () => {
    return renderInfiniteScrollContent(editorCanvases, 'editorCanvases', (editorCanvas, index) => {
      const title = `${_.get(
        editorCanvas,
        "metadata.projectTypeName",
        ""
      )} ${_.get(editorCanvas, "metadata.formatName", "")} ${_.get(
        editorCanvas,
        "metadata.templatesCategoryName",
        ""
      )}`.trim();

      const url = `${
        apiConfig.giottoUrl[isProd ? "prod" : "dev"]
      }/canvas/${editorCanvas.objectId}`;

      // Convert GraphQL structure to match the expected format
      const pages = editorCanvas.editor_canvas_pages?.edges?.map(edge => edge.node) || [];
      
      // Get org info if available
      const orgName = editorCanvas.org_pointer?.nome;
      const orgUrl = editorCanvas.org_pointer?.objectId 
        ? `/orgs/${editorCanvas.org_pointer.objectId}`
        : null;

      return (
        <GiottoPreview
          key={editorCanvas.objectId || index}
          objectId={editorCanvas.objectId}
          url={url}
          title={title}
          createdAt={editorCanvas.createdAt}
          updatedAt={editorCanvas.updatedAt}
          status={editorCanvas.status}
          pages={pages}
          orgName={orgName}
          orgUrl={orgUrl}
          showOrg={isGlobalView}
          isProd={isProd}
        />
      );
    });
  };

  const content = showTabs ? (
    <Tabs defaultActiveKey="Progetti">
      <TabPane tab={`Progetti (${counts.editorCanvases})`} key="Progetti">
        {renderProjectsContent()}
      </TabPane>
      
      {!isGlobalView && (
        <>
          <TabPane tab={`Templates (${counts.editorTemplates})`} key="Templates">
            {renderInfiniteScrollContent(editorTemplates, 'editorTemplates', (editorTemplate, index) => {
              const url = `${
                apiConfig.giottoUrl[isProd ? "prod" : "dev"]
              }/template-manager/${editorTemplate.objectId}`;

              // Convert GraphQL structure to match the expected format
              const pages = editorTemplate.editor_templates_pages?.edges?.map(edge => edge.node) || [];

              return (
                <GiottoPreview
                  key={editorTemplate.objectId || index}
                  objectId={editorTemplate.objectId}
                  url={url}
                  title={editorTemplate.name || "Template"}
                  createdAt={editorTemplate.createdAt}
                  updatedAt={editorTemplate.updatedAt}
                  status={editorTemplate.status || "active"}
                  pages={pages}
                  isProd={isProd}
                />
              );
            })}
          </TabPane>
          
          <TabPane tab={`Images (${counts.giottoImages})`} key="giottoImages">
            {renderInfiniteScrollContent(giottoImages, 'giottoImages', (giottoImage, index) => (
              <AssetsPreview 
                key={giottoImage.objectId || index} 
                imageUrl={giottoImage.file?.url || giottoImage.custom_preview?.url} 
                isProd={isProd}
              />
            ))}
          </TabPane>
          
          <TabPane
            tab={`Backgrounds (${counts.giottoBackgrounds})`}
            key="giottoBackgrounds"
          >
            {renderInfiniteScrollContent(giottoBackgrounds, 'giottoBackgrounds', (giottoBackground, index) => (
              <AssetsPreview
                key={giottoBackground.objectId || index}
                imageUrl={giottoBackground.file?.url}
                isProd={isProd}
              />
            ))}
          </TabPane>
        </>
      )}
    </Tabs>
  ) : (
    renderProjectsContent()
  );

  return (
    <div style={{ margin: isGlobalView ? "16px 16px" : "0" }}>
      {showBreadcrumb && isGlobalView && (
        <>
          <Breadcrumb style={{ marginBottom: "20px" }}>
            <Breadcrumb.Item>Giotto</Breadcrumb.Item>
            <Breadcrumb.Item>Progetti dei Clienti</Breadcrumb.Item>
          </Breadcrumb>
          <Divider />
        </>
      )}
      
      {showTitle && isGlobalView && (
        <>
          <Title level={3} style={{ marginBottom: "40px" }}>
            <Space>
              Giotto - Progetti dei Clienti
              <Badge overflowCount={5000} count={counts.editorCanvases} showZero />
            </Space>
          </Title>
          <Divider />
        </>
      )}
      
      {content}
    </div>
  );
};

export default GiottoProjects;